import type { ManualChapter } from './types'

export const dashboardChapter: ManualChapter = {
  id: 'dashboard',
  number: 3,
  title: 'Dashboard',
  description: 'Il tuo centro di controllo personale',
  icon: 'HomeIcon',
  sections: [
    {
      id: 'overview',
      title: 'Panoramica Dashboard',
      requirement: '§2.2 Dashboard Utente Personalizzata',
      content: `
## Panoramica Dashboard

La Dashboard è la tua **homepage personale** sulla piattaforma. Qui trovi un riepilogo di tutte le informazioni importanti.

![Dashboard](dashboard.png)
*La dashboard con i widget informativi*

### Cosa Trovi nella Dashboard

La dashboard è organizzata in **widget** (riquadri informativi), ognuno dedicato a un'area specifica:

1. **Statistiche Personali** - Punti, livello, badge
2. **Corsi in Corso** - Progressi formativi
3. **Notifiche** - Aggiornamenti recenti
4. **Eventi in Programma** - Prossimi appuntamenti
5. **Task Force** - Attività dei tuoi gruppi di lavoro
6. **Classifica** - La tua posizione nel leaderboard

### Navigazione

Dalla dashboard puoi accedere rapidamente a tutte le sezioni tramite:
- Il **menu laterale** (sidebar) a sinistra
- I **link rapidi** presenti nei widget
- Le **notifiche** cliccabili
`,
      screenshots: [
        {
          id: 'dashboard',
          path: '/assets/manual/dashboard.png',
          caption: 'Dashboard principale',
          alt: 'Dashboard con widget informativi'
        }
      ]
    },
    {
      id: 'stats-widget',
      title: 'Widget Statistiche',
      requirement: '§2.2 Classifica e punteggio gamification',
      content: `
## Widget Statistiche

Il widget statistiche ti mostra a colpo d'occhio i tuoi progressi sulla piattaforma.

### Informazioni Visualizzate

| Dato | Descrizione |
|------|-------------|
| **Punti Totali** | Somma di tutti i punti guadagnati |
| **Livello** | Il tuo livello attuale (ogni 500 punti = 1 livello) |
| **Badge** | Numero di badge ottenuti |
| **Posizione Classifica** | La tua posizione nel leaderboard |

### Come Guadagnare Punti

Ogni azione sulla piattaforma ti assegna punti:

| Azione | Punti |
|--------|-------|
| Completare un corso | 100 |
| Superare un quiz | 50 |
| Creare una discussione | 10 |
| Rispondere nel forum | 5 |
| Partecipare a un evento | 20 |
| Completare un'Escape Room | 50-200 |
| Vincere un torneo | 100+ |
`
    },
    {
      id: 'courses-widget',
      title: 'Widget Corsi',
      requirement: '§2.2 Avanzamento formativo',
      content: `
## Widget Corsi in Corso

Questo widget mostra i tuoi **progressi formativi**.

### Informazioni Visualizzate

- **Corsi Attivi**: Elenco dei corsi a cui sei iscritto
- **Percentuale Completamento**: Barra di progresso per ogni corso
- **Ultima Lezione**: L'ultima lezione completata
- **Prossimo Passo**: La lezione successiva da completare

### Azioni Rapide

- **Continua**: Riprendi dall'ultima lezione
- **Vai al Corso**: Apri la pagina del corso completa
- **Vedi Tutti**: Accedi al catalogo completo dei tuoi corsi
`
    },
    {
      id: 'notifications-widget',
      title: 'Widget Notifiche',
      requirement: '§2.2 Notifiche intelligenti',
      content: `
## Widget Notifiche

Le notifiche ti tengono aggiornato su tutto ciò che accade sulla piattaforma.

![Centro Notifiche](notifications.png)
*Il centro notifiche con gli aggiornamenti recenti*

### Tipi di Notifiche

La piattaforma gestisce **11 tipi di notifiche**:

| Tipo | Icona | Descrizione |
|------|-------|-------------|
| **Forum** | 💬 | Nuove risposte alle tue discussioni |
| **Menzione** | @ | Qualcuno ti ha menzionato |
| **Corso** | 📚 | Nuovo corso disponibile o scadenza |
| **Quiz** | ✓ | Risultato di un quiz |
| **Evento** | 📅 | Promemoria eventi prenotati |
| **Task Force** | 👥 | Aggiornamenti sui tuoi gruppi |
| **Ticket** | 🎫 | Modifiche ai task assegnati |
| **Clinic** | 🏥 | Risposta a una tua domanda |
| **Badge** | 🏆 | Nuovo badge ottenuto |
| **Livello** | ⬆️ | Raggiunto nuovo livello |
| **Sistema** | ⚙️ | Comunicazioni dalla piattaforma |

### Gestire le Notifiche

- **Clicca** su una notifica per andare al contenuto correlato
- **Segna come letta** cliccando sull'icona ✓
- **Segna tutte come lette** per pulire la lista
- **Configura** quali notifiche ricevere nel Profilo
`,
      screenshots: [
        {
          id: 'notifications',
          path: '/assets/manual/notifications.png',
          caption: 'Centro notifiche',
          alt: 'Pagina con elenco delle notifiche'
        }
      ]
    },
    {
      id: 'events-widget',
      title: 'Widget Eventi',
      requirement: '§2.2 Eventi prenotati e agenda personale',
      content: `
## Widget Eventi e Agenda

Questo widget mostra i tuoi **prossimi appuntamenti**.

### Informazioni Visualizzate

- **Eventi prenotati**: Lista dei prossimi eventi a cui sei iscritto
- **Data e ora**: Quando si terrà l'evento
- **Tipo**: Webinar, Workshop, Conferenza, etc.
- **Modalità**: Online (Jitsi) o in presenza

### Azioni Rapide

- **Partecipa**: Accedi direttamente alla videoconferenza (per eventi online)
- **Dettagli**: Apri la pagina completa dell'evento
- **Calendario**: Visualizza il calendario completo
`
    },
    {
      id: 'quick-access',
      title: 'Link Rapidi',
      requirement: '§2.2 Accesso rapido alle Task Force',
      content: `
## Link Rapidi e Accesso Veloce

La dashboard offre accesso rapido alle sezioni più utilizzate.

### Sezioni Principali

Dal menu laterale puoi accedere a:

| Icona | Sezione | Descrizione |
|-------|---------|-------------|
| 🏠 | Dashboard | Torna alla home |
| 💬 | Forum | Discussioni e community |
| 📚 | Corsi | Formazione e certificazioni |
| 📄 | Documenti | Repository documentale |
| 📅 | Eventi | Calendario e prenotazioni |
| 👥 | Task Force | Gruppi di lavoro |
| 🏥 | Compliance Clinic | Consulenza esperti |
| 🏆 | Tornei | Competizioni |
| ⭐ | Classifica | Leaderboard |

### Task Force di Appartenenza

Se fai parte di una o più Task Force, le trovi elencate direttamente nella dashboard per un accesso immediato.
`
    }
  ]
}
